import React, { useCallback, useEffect, useState } from 'react';
import { Plus, Edit, Copy, Trash2, Lock, ShieldCheck, Users } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '../ui/alert-dialog';
import { createRole, deleteRole, getRoleCatalog, getRoles, updateRole } from '../../services/api';
import { COLUMNS, SCOPE_OPTIONS, countGranted, permissionValue, setPermission } from '../../lib/roles';

const EMPTY_FORM = { key: null, name: '', description: '', permissions: {} };

const PermissionControl = ({ permission, scoped, permissions, readOnly, onChange, label }) => {
  if (!permission) {
    return <span className="text-slate-300 text-sm" aria-label={`${label} : sans objet`}>—</span>;
  }
  const value = permissionValue(permissions, permission, scoped);
  if (scoped) {
    return (
      <Select value={value} onValueChange={(next) => onChange(permission, next)} disabled={readOnly}>
        <SelectTrigger className="h-9 text-sm" aria-label={label}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {SCOPE_OPTIONS.map((option) => (
            <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }
  return (
    <Checkbox
      checked={value}
      onCheckedChange={(checked) => onChange(permission, checked === true)}
      disabled={readOnly}
      aria-label={label}
      className="data-[state=checked]:bg-[#00b4d8] data-[state=checked]:border-[#00b4d8]"
    />
  );
};

export const RolesPanel = () => {
  const [roles, setRoles] = useState([]);
  const [catalog, setCatalog] = useState({ modules: [], scoped: [] });
  const [loading, setLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [readOnly, setReadOnly] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const load = useCallback(async () => {
    try {
      const [rolesResponse, catalogResponse] = await Promise.all([getRoles(), getRoleCatalog()]);
      setRoles(rolesResponse.data || []);
      setCatalog(catalogResponse.data || { modules: [], scoped: [] });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Impossible de charger les rôles');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const scoped = new Set(catalog.scoped || []);

  const openEditor = (role, mode) => {
    if (mode === 'duplicate') {
      setForm({ key: null, name: `${role.name} (copie)`, description: role.description, permissions: { ...role.permissions } });
    } else if (role) {
      setForm({ key: role.key, name: role.name, description: role.description, permissions: { ...role.permissions } });
    } else {
      setForm(EMPTY_FORM);
    }
    setReadOnly(Boolean(role?.locked) && mode !== 'duplicate');
    setEditorOpen(true);
  };

  const changePermission = (permission, value) =>
    setForm((current) => ({ ...current, permissions: setPermission(current.permissions, permission, value) }));

  const save = async (event) => {
    event.preventDefault();
    if (readOnly || saving) return;
    setSaving(true);
    const payload = { name: form.name.trim(), description: form.description.trim(), permissions: form.permissions };
    try {
      if (form.key) {
        await updateRole(form.key, payload);
        toast.success('Rôle mis à jour; les droits s’appliquent immédiatement');
      } else {
        await createRole(payload);
        toast.success('Rôle créé');
      }
      setEditorOpen(false);
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l’enregistrement du rôle');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      await deleteRole(toDelete.key);
      toast.success('Rôle supprimé');
      setToDelete(null);
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Suppression impossible');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-[#00b4d8] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="roles-panel">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <p className="text-slate-500 max-w-2xl">
          Chaque personne a un rôle. Le rôle décide ce qu’elle peut voir, créer, modifier ou supprimer.
          « Ses dossiers » limite l’accès aux dossiers qui lui sont assignés.
        </p>
        <Button
          onClick={() => openEditor(null)}
          className="rounded-full bg-[#00b4d8] hover:bg-[#009bb8] shrink-0"
          data-testid="add-role-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nouveau rôle
        </Button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {roles.map((role) => (
          <Card key={role.key} className="card-hover" data-testid={`role-card-${role.key}`}>
            <CardContent className="p-6 flex flex-col h-full">
              <div className="flex items-start gap-4">
                <div className={`w-12 h-12 shrink-0 rounded-full flex items-center justify-center text-white ${role.locked ? 'bg-purple-600' : 'bg-[#1a3a5c]'}`}>
                  {role.locked ? <Lock className="w-5 h-5" /> : <ShieldCheck className="w-5 h-5" />}
                </div>
                <div className="min-w-0">
                  <h3 className="font-semibold text-[#1a3a5c]">{role.name}</h3>
                  <p className="text-sm text-slate-500 flex items-center gap-1">
                    <Users className="w-4 h-4" />
                    {role.user_count} personne{role.user_count > 1 ? 's' : ''}
                    {' · '}
                    {role.locked ? 'Accès complet' : `${countGranted(role.permissions)} permission(s)`}
                  </p>
                </div>
              </div>
              {role.description && <p className="text-sm text-slate-600 mt-3 flex-1">{role.description}</p>}
              <div className="mt-4 pt-4 border-t flex flex-wrap gap-2 justify-end">
                <Button variant="outline" size="sm" onClick={() => openEditor(role)} data-testid={`edit-role-${role.key}`}>
                  <Edit className="w-4 h-4 mr-1" />{role.locked ? 'Voir' : 'Modifier'}
                </Button>
                <Button variant="outline" size="sm" onClick={() => openEditor(role, 'duplicate')} aria-label={`Dupliquer ${role.name}`}>
                  <Copy className="w-4 h-4" />
                </Button>
                {role.deletable && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-red-600 hover:bg-red-50 hover:text-red-700"
                    onClick={() => setToDelete(role)}
                    disabled={role.user_count > 0}
                    title={role.user_count > 0 ? 'Attribuez un autre rôle à ces personnes avant de supprimer' : undefined}
                    aria-label={`Supprimer ${role.name}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={editorOpen} onOpenChange={(open) => !saving && setEditorOpen(open)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {readOnly ? `${form.name} — accès complet` : form.key ? 'Modifier le rôle' : 'Nouveau rôle'}
            </DialogTitle>
          </DialogHeader>
          {readOnly && (
            <p className="text-sm text-slate-500">
              Le rôle Administrateur est verrouillé : il peut tout faire, y compris gérer les rôles.
              Dupliquez-le pour créer un rôle proche.
            </p>
          )}
          <form onSubmit={save} className="space-y-5">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Nom du rôle *</Label>
                <Input
                  value={form.name}
                  onChange={(event) => setForm({ ...form, name: event.target.value })}
                  required
                  minLength={2}
                  disabled={readOnly}
                  data-testid="role-name-input"
                />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea
                  value={form.description}
                  onChange={(event) => setForm({ ...form, description: event.target.value })}
                  disabled={readOnly}
                  className="min-h-[40px]"
                />
              </div>
            </div>

            <div className="space-y-3">
              {catalog.modules.map((module) => (
                <div key={module.key} className="rounded-xl border border-slate-200 p-4" data-testid={`module-${module.key}`}>
                  <div className="grid grid-cols-2 sm:grid-cols-[minmax(150px,1.2fr)_repeat(4,minmax(0,1fr))] gap-3 items-center">
                    <p className="col-span-2 sm:col-span-1 font-semibold text-[#1a3a5c]">{module.label}</p>
                    {COLUMNS.map((column) => (
                      <div key={column.key} className="flex items-center justify-between sm:justify-start gap-2">
                        <span className="text-xs text-slate-500 sm:hidden">{column.label}</span>
                        <div className="sm:w-full">
                          <span className="hidden sm:block text-xs text-slate-500 mb-1">{column.label}</span>
                          <PermissionControl
                            permission={module[column.key]}
                            scoped={scoped.has(module[column.key])}
                            permissions={form.permissions}
                            readOnly={readOnly}
                            onChange={changePermission}
                            label={`${module.label} — ${column.label}`}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                  {module.extras.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-slate-100 grid sm:grid-cols-2 gap-2">
                      {module.extras.map((extra) => (
                        <label key={extra.key} className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                          <PermissionControl
                            permission={extra.key}
                            scoped={false}
                            permissions={form.permissions}
                            readOnly={readOnly}
                            onChange={changePermission}
                            label={extra.label}
                          />
                          {extra.label}
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setEditorOpen(false)} disabled={saving}>
                {readOnly ? 'Fermer' : 'Annuler'}
              </Button>
              {!readOnly && (
                <Button type="submit" className="bg-[#00b4d8] hover:bg-[#009bb8]" disabled={saving} data-testid="save-role-btn">
                  {saving ? 'Enregistrement...' : form.key ? 'Mettre à jour' : 'Créer le rôle'}
                </Button>
              )}
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={Boolean(toDelete)} onOpenChange={(open) => !open && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer le rôle « {toDelete?.name} »?</AlertDialogTitle>
            <AlertDialogDescription>
              Aucune personne n’a ce rôle. Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={(event) => { event.preventDefault(); confirmDelete(); }}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default RolesPanel;
